import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { NavBar } from 'components/NavBar/NavBar'
import { Alert, AlertType } from 'components/Alert/Alert'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import {
  ColorVariant,
  Flex,
  H1WithBack,
  PageWrapper,
  TablesGrid,
  colors,
} from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Card, CardBody } from 'components/Card/Card'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { Cell } from 'components/HonestTable/HonestTable.styled'
import { Input } from 'components/Input/Input'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { AppDispatch, RootState } from 'store'
import {
  bulkCancelFoodRecords,
  clearSelectedFoodWindow,
  confirmFoodKitchen,
  createFoodWindowAdjustment,
  getFoodDietaryTags,
  getFoodWindow,
  getFoodWindowAdjustments,
  getFoodWindowEmployees,
} from 'features/food/foodSlice'
import {
  FoodMealWindowRunStatus,
  FoodProvisioningRecordSourceType,
  FoodProvisioningRecordStatus,
  IConfirmFoodKitchenRequest,
  ICreateFoodManualAdjustmentRequest,
  IFoodEmployeeForWindowResponse,
  IFoodKitchenWindowSummaryResponse,
} from 'types/api/foodApi.interface'
import { foodProvisioningModeLabel } from '../foodPlanningData'
import { FoodDrawer, FoodDrawerOverlay } from '../Food.styled'
import { AdjustPlanModal } from '../components/AdjustPlanModal'
import { CancelMealModal } from '../components/CancelMealModal'
import { KitchenConfirmationModal } from '../components/KitchenConfirmationModal'

const recordStatusLabel = (status: number) => {
  if (status === FoodProvisioningRecordStatus.Planned) return 'Planned'
  if (status === FoodProvisioningRecordStatus.Ordered) return 'Ordered'
  if (status === FoodProvisioningRecordStatus.Served) return 'Served'
  if (status === FoodProvisioningRecordStatus.Finalized) return 'Finalized'
  if (status === FoodProvisioningRecordStatus.Cancelled) return 'Cancelled'
  return `Status ${status}`
}

const recordStatusPill = (status: number) => {
  if (
    status === FoodProvisioningRecordStatus.Served ||
    status === FoodProvisioningRecordStatus.Ordered ||
    status === FoodProvisioningRecordStatus.Finalized
  )
    return 'success'
  if (status === FoodProvisioningRecordStatus.Planned) return 'info'
  if (status === FoodProvisioningRecordStatus.Cancelled) return 'danger'
  return 'accent'
}

const sourceLabel = (source: number) => {
  if (source === FoodProvisioningRecordSourceType.Entitled) return 'Entitled'
  if (source === FoodProvisioningRecordSourceType.AdHoc) return 'Ad-hoc'
  if (source === FoodProvisioningRecordSourceType.StandBy) return 'Standby'
  if (source === FoodProvisioningRecordSourceType.ManualAdjustment) return 'Manual'
  return `Source ${source}`
}

const windowRunStatusLabel = (status: number | null) => {
  if (status === FoodMealWindowRunStatus.Open) return 'Open'
  if (status === FoodMealWindowRunStatus.Ordered) return 'Ordered'
  if (status === FoodMealWindowRunStatus.Served) return 'Served'
  if (status === FoodMealWindowRunStatus.Finalized) return 'Finalized'
  return 'No run'
}

const personName = (row: IFoodEmployeeForWindowResponse) =>
  row.person ? `${row.person.firstName} ${row.person.lastName}` : '—'

export const FoodWindowDetail = () => {
  const { id } = useParams()
  const runId = Number(id)
  const dispatch = useDispatch<AppDispatch>()
  const { selectedWindow, windowEmployees, windowAdjustments, dietaryTags, isLoading } =
    useSelector((store: RootState) => store.food)

  const [selectedPerson, setSelectedPerson] = useState<IFoodEmployeeForWindowResponse | null>(null)
  const [confKitchen, setConfKitchen] = useState<IFoodKitchenWindowSummaryResponse | null>(null)
  const [cancelFor, setCancelFor] = useState<IFoodEmployeeForWindowResponse | null>(null)
  const [adjustOpen, setAdjustOpen] = useState(false)
  const [search, setSearch] = useState('')

  const refresh = () => {
    if (!runId) return
    dispatch(getFoodWindow(runId))
    dispatch(getFoodWindowEmployees({ runId, params: { page: 1, limit: 200, search } }))
    dispatch(getFoodWindowAdjustments(runId))
  }

  useEffect(() => {
    dispatch(getFoodDietaryTags({ page: 1, limit: 200 }))
    return () => {
      dispatch(clearSelectedFoodWindow())
    }
  }, [dispatch])

  useEffect(() => {
    if (!runId) return
    refresh()
  }, [dispatch, runId])

  useEffect(() => {
    if (!runId) return
    const t = setTimeout(() => {
      dispatch(getFoodWindowEmployees({ runId, params: { page: 1, limit: 200, search } }))
    }, 300)
    return () => clearTimeout(t)
  }, [dispatch, runId, search])

  const w = selectedWindow
  const locked = w?.status !== null && w?.status !== FoodMealWindowRunStatus.Open

  const handleAdjust = async (body: ICreateFoodManualAdjustmentRequest) => {
    const resp = await dispatch(createFoodWindowAdjustment({ runId, body }))
    if (resp.meta.requestStatus === 'fulfilled') {
      setAdjustOpen(false)
      refresh()
    }
  }

  const handleConfirm = async (body: IConfirmFoodKitchenRequest) => {
    if (!confKitchen?.kitchen) return
    const resp = await dispatch(
      confirmFoodKitchen({ runId, kitchenId: confKitchen.kitchen.id, body }),
    )
    if (resp.meta.requestStatus === 'fulfilled') {
      setConfKitchen(null)
      refresh()
    }
  }

  const handleSingleCancel = async (reason: string) => {
    if (!cancelFor) return
    const resp = await dispatch(
      bulkCancelFoodRecords({ recordIds: [cancelFor.recordId], reason }),
    )
    if (resp.meta.requestStatus === 'fulfilled') {
      setCancelFor(null)
      setSelectedPerson(null)
      refresh()
    }
  }

  const peopleColumns: IHonestTableColumn<IFoodEmployeeForWindowResponse>[] = [
    {
      id: 'name',
      header: 'Name',
      sticky: true,
      minWidth: '12rem',
      cell: (p) => (
        <Cell>
          <BodyText weight='600'>{personName(p)}</BodyText>
        </Cell>
      ),
    },
    { id: 'dept', header: 'Department', cell: (p) => <BodyText>{p.departmentName || '—'}</BodyText> },
    {
      id: 'mode',
      header: 'Mode',
      cell: (p) => (
        <BodyText>
          {p.mode != null ? foodProvisioningModeLabel(p.mode) : '—'}
        </BodyText>
      ),
    },
    { id: 'source', header: 'Source', cell: (p) => <BodyText>{sourceLabel(p.sourceType)}</BodyText> },
    {
      id: 'kitchen',
      header: 'Kitchen',
      cell: (p) => <BodyText>{p.kitchen?.name || '—'}</BodyText>,
    },
    {
      id: 'dietary',
      header: 'Dietary',
      cell: (p) => (
        <Flex wrap gap='0.25rem'>
          {(p.dietaryTags || []).map((t) => (
            <StatusPill key={t.code || t.id} status='accent'>
              {t.code || t.name}
            </StatusPill>
          ))}
          {!p.dietaryTags?.length && <OtherText grey>—</OtherText>}
        </Flex>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (p) => (
        <StatusPill status={recordStatusPill(p.status)}>{recordStatusLabel(p.status)}</StatusPill>
      ),
    },
  ]

  if (!runId) {
    return (
      <>
        <NavBar />
        <PageWrapper noFooter>
          <H1WithBack arrow title='Window' link='/hr/food' />
          <OtherText grey>Invalid window id</OtherText>
        </PageWrapper>
      </>
    )
  }

  return (
    <>
      <NavBar />
      <PageWrapper noFooter>
        <Flex between center width='100%'>
          <H1WithBack arrow title={w?.windowDef?.name || 'Window'} link='/hr/food' />
          <Flex gap='0.5rem'>
            <Button
              variant='secondary'
              disabled={!w || w.status === FoodMealWindowRunStatus.Finalized}
              onClick={() => setAdjustOpen(true)}
            >
              Adjust Plan
            </Button>
          </Flex>
        </Flex>

        {w && (
          <Flex center gap='0.75rem' mt='8'>
            <StatusPill
              status={
                w.status === FoodMealWindowRunStatus.Finalized ||
                w.status === FoodMealWindowRunStatus.Served
                  ? 'success'
                  : w.status === FoodMealWindowRunStatus.Ordered
                    ? 'accent'
                    : 'neutral'
              }
            >
              {windowRunStatusLabel(w.status)}
            </StatusPill>
            <OtherText grey>
              {`${String(w.windowDef?.serviceStart || '').slice(0, 5)}–${String(w.windowDef?.serviceEnd || '').slice(0, 5)}`}
              {' · Cut-off '}
              {String(w.windowDef?.cutoffTime || '').slice(0, 5)}
              {locked && (
                <OtherText color={colors.red}> (Passed — per-kitchen hot orders locked)</OtherText>
              )}
            </OtherText>
          </Flex>
        )}

        {w && w.manualAdjustmentQty > 0 && (
          <Alert
            mt='1rem'
            title={`Plan includes manual adjustment: +${w.manualAdjustmentQty}`}
            description={(w.manualAdjustmentReasons || []).join('; ') || 'Manual adjustment'}
            type={AlertType.WARNING}
            colorVariant={ColorVariant.PRIMARY}
          />
        )}

        <Flex column gap='1rem' width='100%' mt='16'>
          <Container>
            <ContainerHeader>
              <TitleText weight='700'>Menu — provided by Kitchen (read-only)</TitleText>
            </ContainerHeader>
            <ContainerContent>
              <TablesGrid>
                {(w?.kitchens || []).map((k) => (
                  <Card key={k.kitchen?.id}>
                    <CardBody>
                      <BodyText weight='600'>{k.kitchen?.name}</BodyText>
                      {(k.menuEntries || []).map((entry, idx) => (
                        <OtherText key={idx} grey>
                          {(entry.dietaryTags || []).map((t) => t.code).join('+') || 'Untagged'}
                          {': '}
                          {entry.dish?.name || '—'}
                        </OtherText>
                      ))}
                      {!k.menuEntries?.length && <OtherText grey>—</OtherText>}
                    </CardBody>
                  </Card>
                ))}
                {!w?.kitchens?.length && <OtherText grey>No kitchens on this window</OtherText>}
              </TablesGrid>
            </ContainerContent>
          </Container>

          <Container>
            <ContainerHeader>
              <TitleText weight='700'>Kitchen Confirmation — per kitchen</TitleText>
            </ContainerHeader>
            <ContainerContent gap='0.5rem'>
              {!locked ? (
                <OtherText grey>
                  Opens after this window&apos;s cut-off (
                  {String(w?.windowDef?.cutoffTime || '').slice(0, 5)}).
                </OtherText>
              ) : (
                (w?.kitchens || []).map((k) => {
                  if (!k.ordered) return null
                  return (
                    <Card key={k.kitchen?.id}>
                      <CardBody>
                        <Flex gap='0.75rem' center wrap>
                          <BodyText weight='700' style={{ minWidth: '9rem' }}>
                            {k.kitchen?.name}
                          </BodyText>
                          <OtherText grey>Ordered: {k.ordered}</OtherText>
                          {k.confirmed ? (
                            <Flex gap='0.5rem' center>
                              <StatusPill status='success'>
                                {`${k.served} served · ${k.standbyUsed} standby used`}
                              </StatusPill>
                            </Flex>
                          ) : (
                            <Flex gap='0.5rem' center width='100%' between>
                              <StatusPill status='accent'>Awaiting confirmation</StatusPill>
                              <Button
                                variant='primary'
                                size='sm'
                                onClick={() => setConfKitchen(k)}
                              >
                                Enter Confirmation
                              </Button>
                            </Flex>
                          )}
                        </Flex>
                      </CardBody>
                    </Card>
                  )
                })
              )}
            </ContainerContent>
          </Container>

          {!!windowAdjustments?.length && (
            <Container>
              <ContainerHeader>
                <TitleText weight='700'>Adjustments</TitleText>
              </ContainerHeader>
              <ContainerContent gap='0.5rem'>
                {windowAdjustments.map((adj) => (
                  <OtherText key={adj.id} grey>
                    {adj.reason}
                    {' — '}
                    {(adj.lines || [])
                      .map(
                        (l) =>
                          `${l.kitchen?.name || '?'} ×${l.qty} (${(l.dietaryTags || [])
                            .map((t) => t.code)
                            .join('+') || 'untagged'})`,
                      )
                      .join(', ')}
                  </OtherText>
                ))}
              </ContainerContent>
            </Container>
          )}

          <Container>
            <ContainerHeader padding>
              <Flex between center width='100%' wrap gap='0.75rem'>
                <TitleText weight='700'>Employee List</TitleText>
                <Input
                  placeholder='Search employee…'
                  value={search}
                  onChange={(e: any) => setSearch(e.target.value)}
                  nomarg
                  width='15rem'
                  search
                />
              </Flex>
            </ContainerHeader>
            <ContainerContent>
              <HonestTable
                columns={peopleColumns}
                data={windowEmployees.data || []}
                keyField='recordId'
                loading={isLoading}
                onClick={(p) => setSelectedPerson(p)}
                emptyMessage='No employees for this window'
              />
            </ContainerContent>
          </Container>
        </Flex>

        {selectedPerson && (
          <>
            <FoodDrawerOverlay onClick={() => setSelectedPerson(null)} />
            <FoodDrawer>
              <Flex between width='100%' center>
                <TitleText weight='bold'>
                  Person Details — {w?.windowDef?.name || 'Window'}
                </TitleText>
                <CloseIcon onClick={() => setSelectedPerson(null)} />
              </Flex>
              <Flex column gap='0.5rem' mt='16'>
                <OtherText grey>NAME</OtherText>
                <BodyText weight='700'>{personName(selectedPerson)}</BodyText>
              </Flex>
              <TablesGrid>
                {[
                  ['DEPARTMENT', selectedPerson.departmentName || '—'],
                  ['SOURCE', sourceLabel(selectedPerson.sourceType)],
                  ['KITCHEN', selectedPerson.kitchen?.name || '—'],
                  [
                    'MODE',
                    selectedPerson.mode != null
                      ? foodProvisioningModeLabel(selectedPerson.mode)
                      : '—',
                  ],
                ].map(([l, v]) => (
                  <Flex key={l} column gap='0.25rem'>
                    <OtherText grey>{l}</OtherText>
                    <BodyText>{v}</BodyText>
                  </Flex>
                ))}
              </TablesGrid>
              <Flex column gap='0.5rem' mt='16'>
                <OtherText grey>DIETARY REQUIREMENT (tag set)</OtherText>
                <Flex wrap gap='0.25rem'>
                  {(selectedPerson.dietaryTags || []).map((t) => (
                    <StatusPill key={t.code || t.id} status='accent'>
                      {t.code || t.name}
                    </StatusPill>
                  ))}
                  {!selectedPerson.dietaryTags?.length && <OtherText grey>—</OtherText>}
                </Flex>
              </Flex>
              <Flex mt='16'>
                <StatusPill status={recordStatusPill(selectedPerson.status)}>
                  {recordStatusLabel(selectedPerson.status)}
                </StatusPill>
              </Flex>
              {selectedPerson.status !== FoodProvisioningRecordStatus.Cancelled &&
                selectedPerson.status !== FoodProvisioningRecordStatus.Finalized && (
                  <Flex gap='0.5rem' mt='16'>
                    <Button variant='primary' onClick={() => setCancelFor(selectedPerson)}>
                      Cancel Meal Intent
                    </Button>
                  </Flex>
                )}
              <Flex column gap='0.25rem' mt='16'>
                <OtherText grey>RECORD CHAIN (append-only)</OtherText>
                {(selectedPerson.recordChain || []).map((c, i) => (
                  <OtherText key={i} grey>
                    {recordStatusLabel(c.status)}
                    {c.reason ? ` · ${c.reason}` : ''}
                    {c.at ? ` · ${String(c.at).slice(0, 19).replace('T', ' ')}` : ''}
                  </OtherText>
                ))}
                {!selectedPerson.recordChain?.length && <OtherText grey>—</OtherText>}
              </Flex>
            </FoodDrawer>
          </>
        )}

        {adjustOpen && w && (
          <AdjustPlanModal
            window={w}
            dietaryTags={dietaryTags.data || []}
            closeModal={() => setAdjustOpen(false)}
            onSubmit={handleAdjust}
          />
        )}

        {cancelFor && (
          <CancelMealModal
            title='Cancel Meal Intent'
            description={`Cancel ${personName(cancelFor)}'s meal at ${cancelFor.kitchen?.name || 'kitchen'}?`}
            locked={!!locked}
            closeModal={() => setCancelFor(null)}
            onConfirm={handleSingleCancel}
          />
        )}

        {confKitchen && w && (
          <KitchenConfirmationModal
            kitchenName={confKitchen.kitchen?.name || 'Kitchen'}
            windowName={w.windowDef?.name || 'Window'}
            ordered={confKitchen.ordered}
            standbyConfigs={confKitchen.standby || []}
            closeModal={() => setConfKitchen(null)}
            onSubmit={handleConfirm}
          />
        )}
      </PageWrapper>
    </>
  )
}
